import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Settings, Loader2, CheckCircle2, Save, Plus, X, Clock, Shield,
  Mail, Send, Zap, Timer, Users, Ban, BarChart3, Repeat,
  AlertTriangle, Target, MousePointerClick, Eye, Filter, Trash2, Pencil
} from "lucide-react";

const TRIGGER_EVENTS = [
  { value: "new_client", label: "Nouveau client", desc: "Email envoye a l'inscription" },
  { value: "inactivity", label: "Inactivite", desc: "Email apres X jours sans activite" },
  { value: "anniversary", label: "Anniversaire", desc: "Email pour l'anniversaire client" },
  { value: "post_signup_drip", label: "Sequence d'accueil", desc: "Serie d'emails post-inscription" },
  { value: "abandoned_action", label: "Action abandonnee", desc: "Relance apres action incomplete" },
  { value: "custom", label: "Personnalise", desc: "Declencheur defini manuellement" },
];

const WINNING_CRITERIA = [
  { value: "open_rate", label: "Taux d'ouverture" },
  { value: "click_rate", label: "Taux de clic" },
];

function Toggle({ value, onChange, testId }) {
  return (
    <button onClick={() => onChange(!value)}
      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${value ? "bg-[#3b82f6]" : "bg-slate-600"}`}
      data-testid={testId}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export function MailingSettings({ api }) {
  const [settings, setSettings] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newBlacklist, setNewBlacklist] = useState("");
  const [settingsTab, setSettingsTab] = useState("general");

  // Automation dialog
  const [showAutoDialog, setShowAutoDialog] = useState(false);
  const [editAuto, setEditAuto] = useState(null);
  const [autoForm, setAutoForm] = useState({
    name: "", type: "trigger", trigger_event: "new_client", trigger_delay_days: 0,
    subject: "", content_html: "", recipients_type: "all_clients", recipients_tags: [],
    sequence_steps: [], recurring_schedule: { frequency: "weekly", day: "lundi", time: "09:00" },
    ab_test: { enabled: false, variant_b_subject: "", variant_b_content: "" }, enabled: true,
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/marketing/mailing-settings"),
        api.get("/marketing/automations").catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      setAutomations(a.data || []);
    } catch {
      setSettings({
        send_limits: { daily_max: 500, hourly_max: 100, pause_between_sends_seconds: 2 },
        send_window: { enabled: false, start_hour: "08:00", end_hour: "19:00", timezone: "Europe/Paris", exclude_weekends: true },
        tracking: { track_opens: true, track_clicks: true, track_bounces: true },
        unsubscribe: { include_link: true, custom_text: "Pour vous desinscrire, cliquez ici." },
        blacklist: [], sender: { name: "", email: "", reply_to: "" },
        ab_testing: { enabled: false, test_percentage: 20, winning_criteria: "open_rate", auto_send_winner_after_hours: 4 },
      });
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/marketing/mailing-settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const updateNested = (path, value) => {
    setSettings(s => {
      const copy = JSON.parse(JSON.stringify(s));
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const addBlacklist = () => {
    if (!newBlacklist.trim()) return;
    setSettings(s => ({ ...s, blacklist: [...(s.blacklist || []), newBlacklist.trim()] }));
    setNewBlacklist("");
  };

  const removeBlacklist = (email) => {
    setSettings(s => ({ ...s, blacklist: (s.blacklist || []).filter(e => e !== email) }));
  };

  // Automations
  const openNewAuto = () => {
    setEditAuto(null);
    setAutoForm({
      name: "", type: "trigger", trigger_event: "new_client", trigger_delay_days: 0,
      subject: "", content_html: "", recipients_type: "all_clients", recipients_tags: [],
      sequence_steps: [{ delay_days: 0, subject: "", content_html: "" }],
      recurring_schedule: { frequency: "weekly", day: "lundi", time: "09:00" },
      ab_test: { enabled: false, variant_b_subject: "", variant_b_content: "" }, enabled: true,
    });
    setShowAutoDialog(true);
  };

  const openEditAuto = (a) => {
    setEditAuto(a);
    setAutoForm({
      name: a.name || "", type: a.type || "trigger", trigger_event: a.trigger_event || "new_client",
      trigger_delay_days: a.trigger_delay_days || 0, subject: a.subject || "", content_html: a.content_html || "",
      recipients_type: a.recipients_type || "all_clients", recipients_tags: a.recipients_tags || [],
      sequence_steps: a.sequence_steps?.length > 0 ? a.sequence_steps : [{ delay_days: 0, subject: "", content_html: "" }],
      recurring_schedule: a.recurring_schedule || { frequency: "weekly", day: "lundi", time: "09:00" },
      ab_test: a.ab_test || { enabled: false, variant_b_subject: "", variant_b_content: "" },
      enabled: a.enabled !== false,
    });
    setShowAutoDialog(true);
  };

  const saveAutomation = async () => {
    setSaving(true);
    try {
      if (editAuto) {
        await api.put(`/marketing/automations/${editAuto.automation_id}`, autoForm);
      } else {
        await api.post("/marketing/automations", autoForm);
      }
      setShowAutoDialog(false);
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteAutomation = async (id) => {
    if (!window.confirm("Supprimer cette automation ?")) return;
    await api.delete(`/marketing/automations/${id}`);
    loadAll();
  };

  const toggleAutomation = async (a) => {
    await api.put(`/marketing/automations/${a.automation_id}`, { enabled: !a.enabled });
    loadAll();
  };

  // Sequence steps
  const addStep = () => setAutoForm(f => ({ ...f, sequence_steps: [...f.sequence_steps, { delay_days: f.sequence_steps.length * 3, subject: "", content_html: "" }] }));
  const updateStep = (idx, field, value) => setAutoForm(f => ({ ...f, sequence_steps: f.sequence_steps.map((s, i) => i === idx ? { ...s, [field]: value } : s) }));
  const removeStep = (idx) => setAutoForm(f => ({ ...f, sequence_steps: f.sequence_steps.filter((_, i) => i !== idx) }));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#3b82f6]" /></div>;
  if (!settings) return null;

  const stabs = [
    { key: "general", label: "General", icon: Settings },
    { key: "envoi", label: "Envoi", icon: Send },
    { key: "tracking", label: "Tracking", icon: BarChart3 },
    { key: "ab", label: "A/B Testing", icon: Target },
    { key: "automations", label: "Automations", icon: Zap },
    { key: "blacklist", label: "Blacklist", icon: Ban },
  ];

  return (
    <div data-testid="mailing-settings" className="max-w-3xl space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center">
          <Settings className="w-4 h-4 text-[#3b82f6]" />
        </div>
        <div>
          <h3 className="text-sm font-bold">Parametres Mailing</h3>
          <p className="text-[10px] text-slate-500">Envoi, tracking, automatisations, segmentation</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-white/10 pb-2 overflow-x-auto">
        {stabs.map(t => (
          <button key={t.key} onClick={() => setSettingsTab(t.key)} data-testid={`mailing-stab-${t.key}`}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
              settingsTab === t.key ? "bg-[#3b82f6] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* ── GENERAL ── */}
      {settingsTab === "general" && (
        <div className="space-y-4">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
            <h4 className="text-xs font-bold mb-3 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#3b82f6]" /> Expediteur</h4>
            <div className="space-y-2">
              <Input value={settings.sender?.name || ""} onChange={e => updateNested("sender.name", e.target.value)}
                placeholder="Nom de l'expediteur" className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="sender-name" />
              <Input value={settings.sender?.email || ""} onChange={e => updateNested("sender.email", e.target.value)}
                placeholder="Email d'envoi (ex: contact@blitzz.fr)" className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="sender-email" />
              <Input value={settings.sender?.reply_to || ""} onChange={e => updateNested("sender.reply_to", e.target.value)}
                placeholder="Email de reponse (reply-to)" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            </div>
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
            <h4 className="text-xs font-bold mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-amber-400" /> Desabonnement</h4>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-300">Inclure le lien de desabonnement</span>
              <Toggle value={settings.unsubscribe?.include_link !== false} onChange={v => updateNested("unsubscribe.include_link", v)} testId="toggle-unsub" />
            </div>
            <Input value={settings.unsubscribe?.custom_text || ""} onChange={e => updateNested("unsubscribe.custom_text", e.target.value)}
              placeholder="Texte de desabonnement" className="bg-[#0b0811] border-white/10 text-white text-xs" />
          </div>
        </div>
      )}

      {/* ── ENVOI ── */}
      {settingsTab === "envoi" && (
        <div className="space-y-4">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
            <h4 className="text-xs font-bold mb-3 flex items-center gap-2"><Timer className="w-3.5 h-3.5 text-purple-400" /> Limites d'envoi</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Max / jour</label>
                <Input type="number" value={settings.send_limits?.daily_max || 500}
                  onChange={e => updateNested("send_limits.daily_max", parseInt(e.target.value) || 0)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="daily-max" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Max / heure</label>
                <Input type="number" value={settings.send_limits?.hourly_max || 100}
                  onChange={e => updateNested("send_limits.hourly_max", parseInt(e.target.value) || 0)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="hourly-max" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Pause (sec)</label>
                <Input type="number" value={settings.send_limits?.pause_between_sends_seconds || 2}
                  onChange={e => updateNested("send_limits.pause_between_sends_seconds", parseInt(e.target.value) || 0)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs" />
              </div>
            </div>
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-blue-400" /> Fenetre d'envoi</h4>
              <Toggle value={settings.send_window?.enabled} onChange={v => updateNested("send_window.enabled", v)} testId="toggle-send-window" />
            </div>
            {settings.send_window?.enabled && (
              <div className="space-y-2 mt-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Debut</label>
                    <Input type="time" value={settings.send_window?.start_hour || "08:00"}
                      onChange={e => updateNested("send_window.start_hour", e.target.value)}
                      className="bg-[#0b0811] border-white/10 text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 block mb-1">Fin</label>
                    <Input type="time" value={settings.send_window?.end_hour || "19:00"}
                      onChange={e => updateNested("send_window.end_hour", e.target.value)}
                      className="bg-[#0b0811] border-white/10 text-white text-xs" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Exclure weekends</span>
                  <Toggle value={settings.send_window?.exclude_weekends !== false} onChange={v => updateNested("send_window.exclude_weekends", v)} testId="toggle-exclude-weekends" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Fuseau horaire</label>
                  <select value={settings.send_window?.timezone || "Europe/Paris"}
                    onChange={e => updateNested("send_window.timezone", e.target.value)}
                    className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs">
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TRACKING ── */}
      {settingsTab === "tracking" && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-3">
          <h4 className="text-xs font-bold flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-[#10b981]" /> Suivi des performances</h4>
          {[
            { key: "track_opens", label: "Suivre les ouvertures", icon: Eye, desc: "Pixel invisible pour mesurer le taux d'ouverture" },
            { key: "track_clicks", label: "Suivre les clics", icon: MousePointerClick, desc: "Redirection via liens de tracking" },
            { key: "track_bounces", label: "Suivre les bounces", icon: AlertTriangle, desc: "Detection des emails non delivres" },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-300">{item.label}</p>
                  <p className="text-[9px] text-slate-600">{item.desc}</p>
                </div>
              </div>
              <Toggle value={settings.tracking?.[item.key] !== false}
                onChange={v => updateNested(`tracking.${item.key}`, v)} testId={`toggle-${item.key}`} />
            </div>
          ))}
        </div>
      )}

      {/* ── A/B TESTING ── */}
      {settingsTab === "ab" && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-bold flex items-center gap-2"><Target className="w-3.5 h-3.5 text-orange-400" /> A/B Testing</h4>
            <Toggle value={settings.ab_testing?.enabled} onChange={v => updateNested("ab_testing.enabled", v)} testId="toggle-ab" />
          </div>
          <p className="text-[10px] text-slate-500">Testez 2 versions de vos emails et envoyez automatiquement la gagnante</p>

          {settings.ab_testing?.enabled && (
            <div className="space-y-3 mt-3 pt-3 border-t border-white/5">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Pourcentage de test</label>
                <div className="flex items-center gap-2">
                  <input type="range" min={5} max={50} value={settings.ab_testing?.test_percentage || 20}
                    onChange={e => updateNested("ab_testing.test_percentage", parseInt(e.target.value))}
                    className="flex-1 accent-[#3b82f6]" data-testid="ab-test-pct" />
                  <span className="text-sm font-bold text-[#3b82f6] w-10 text-center">{settings.ab_testing?.test_percentage || 20}%</span>
                </div>
                <p className="text-[9px] text-slate-600">{settings.ab_testing?.test_percentage || 20}% recoivent la variante A, {settings.ab_testing?.test_percentage || 20}% la B, le reste recoit la gagnante</p>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Critere de victoire</label>
                <div className="flex gap-2">
                  {WINNING_CRITERIA.map(c => (
                    <button key={c.value} onClick={() => updateNested("ab_testing.winning_criteria", c.value)}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                        settings.ab_testing?.winning_criteria === c.value ? "bg-[#3b82f6]/15 text-[#3b82f6] border border-[#3b82f6]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"
                      }`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Envoyer la gagnante apres (heures)</label>
                <select value={settings.ab_testing?.auto_send_winner_after_hours || 4}
                  onChange={e => updateNested("ab_testing.auto_send_winner_after_hours", parseInt(e.target.value))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs">
                  {[1, 2, 4, 6, 12, 24, 48].map(h => <option key={h} value={h}>{h}h</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AUTOMATIONS ── */}
      {settingsTab === "automations" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-yellow-400" /> Automations ({automations.length})</h4>
            <Button onClick={openNewAuto} size="sm" className="bg-[#3b82f6] text-white text-xs" data-testid="add-automation-btn">
              <Plus className="w-3 h-3 mr-1" /> Automation
            </Button>
          </div>

          {automations.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-xs bg-[#14101c] border border-white/5 rounded-xl">
              Aucune automation configuree
            </div>
          ) : (
            automations.map(a => (
              <div key={a.automation_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`auto-${a.automation_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Zap className={`w-3.5 h-3.5 ${a.enabled ? "text-yellow-400" : "text-slate-600"}`} />
                    <span className="text-sm font-bold">{a.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.type === "trigger" ? "bg-purple-500/10 text-purple-400" : a.type === "sequence" ? "bg-blue-500/10 text-blue-400" : "bg-green-500/10 text-green-400"}`}>
                      {a.type === "trigger" ? "Declencheur" : a.type === "sequence" ? "Sequence" : "Recurrent"}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.enabled ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      {a.enabled ? "Actif" : "Inactif"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggleAutomation(a)} className="p-1.5 hover:bg-white/5 rounded" title={a.enabled ? "Desactiver" : "Activer"}>
                      <Zap className={`w-3 h-3 ${a.enabled ? "text-yellow-400" : "text-slate-600"}`} />
                    </button>
                    <button onClick={() => openEditAuto(a)} className="p-1.5 hover:bg-white/5 rounded"><Pencil className="w-3 h-3 text-slate-400" /></button>
                    <button onClick={() => deleteAutomation(a.automation_id)} className="p-1.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500">
                  {a.type === "trigger" && <span>Declencheur : {TRIGGER_EVENTS.find(t => t.value === a.trigger_event)?.label || a.trigger_event}{a.trigger_delay_days > 0 ? ` (J+${a.trigger_delay_days})` : ""}</span>}
                  {a.type === "sequence" && <span>{a.sequence_steps?.length || 0} etapes</span>}
                  {a.type === "recurring" && <span>Recurrence : {a.recurring_schedule?.frequency || "weekly"}</span>}
                  {a.stats?.sent > 0 && <span className="ml-3 text-[#10b981]">{a.stats.sent} envoyes</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── BLACKLIST ── */}
      {settingsTab === "blacklist" && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-3">
          <h4 className="text-xs font-bold flex items-center gap-2"><Ban className="w-3.5 h-3.5 text-red-400" /> Blacklist ({(settings.blacklist || []).length})</h4>
          <p className="text-[10px] text-slate-500">Les emails dans cette liste ne recevront jamais de campagne</p>

          <div className="flex gap-1">
            <Input value={newBlacklist} onChange={e => setNewBlacklist(e.target.value)}
              placeholder="Email a bloquer" className="bg-[#0b0811] border-white/10 text-white text-xs flex-1"
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addBlacklist())} data-testid="blacklist-input" />
            <Button onClick={addBlacklist} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs"><Plus className="w-3 h-3" /></Button>
          </div>

          {(settings.blacklist || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(settings.blacklist || []).map((email, i) => (
                <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-red-500/10 text-red-400 flex items-center gap-1">
                  {email} <button onClick={() => removeBlacklist(email)}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save all (except automations tab which has its own dialog) */}
      {settingsTab !== "automations" && (
        <Button onClick={saveSettings} disabled={saving} className="bg-[#3b82f6] text-white hover:bg-[#2563eb] font-bold" data-testid="save-mailing-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {saved ? "Sauvegarde !" : "Sauvegarder"}
        </Button>
      )}

      {/* ── Automation Dialog ── */}
      <Dialog open={showAutoDialog} onOpenChange={(v) => { setShowAutoDialog(v); if (!v) setEditAuto(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editAuto ? "Modifier l'automation" : "Nouvelle automation"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={autoForm.name} onChange={e => setAutoForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Nom de l'automation *" className="bg-[#0b0811] border-white/10 text-white" data-testid="auto-name" />

            {/* Type */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Type</label>
              <div className="flex gap-2">
                {[
                  { value: "trigger", label: "Declencheur", icon: Zap },
                  { value: "sequence", label: "Sequence", icon: Repeat },
                  { value: "recurring", label: "Recurrent", icon: Clock },
                ].map(t => (
                  <button key={t.value} onClick={() => setAutoForm(f => ({ ...f, type: t.value }))}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium ${
                      autoForm.type === t.value ? "bg-[#3b82f6]/15 text-[#3b82f6] border border-[#3b82f6]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"
                    }`}>
                    <t.icon className="w-3 h-3" /> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Trigger config */}
            {autoForm.type === "trigger" && (
              <div className="space-y-2">
                <label className="text-[10px] text-slate-500 uppercase block">Evenement declencheur</label>
                <select value={autoForm.trigger_event} onChange={e => setAutoForm(f => ({ ...f, trigger_event: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs" data-testid="trigger-event">
                  {TRIGGER_EVENTS.map(t => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}
                </select>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Delai apres evenement (jours)</label>
                  <Input type="number" min={0} value={autoForm.trigger_delay_days}
                    onChange={e => setAutoForm(f => ({ ...f, trigger_delay_days: parseInt(e.target.value) || 0 }))}
                    className="bg-[#0b0811] border-white/10 text-white text-xs" />
                </div>
              </div>
            )}

            {/* Sequence config */}
            {autoForm.type === "sequence" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-slate-500 uppercase">Etapes de la sequence</label>
                  <button onClick={addStep} className="text-[10px] text-[#3b82f6] hover:underline flex items-center gap-0.5">
                    <Plus className="w-3 h-3" /> Etape
                  </button>
                </div>
                {autoForm.sequence_steps.map((step, idx) => (
                  <div key={idx} className="bg-[#0b0811] border border-white/5 rounded-lg p-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400 font-bold">Etape {idx + 1}</span>
                      {autoForm.sequence_steps.length > 1 && (
                        <button onClick={() => removeStep(idx)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <div className="w-20">
                        <label className="text-[9px] text-slate-600">J+</label>
                        <Input type="number" min={0} value={step.delay_days}
                          onChange={e => updateStep(idx, "delay_days", parseInt(e.target.value) || 0)}
                          className="bg-transparent border-white/10 text-white text-xs" />
                      </div>
                      <div className="flex-1">
                        <label className="text-[9px] text-slate-600">Objet</label>
                        <Input value={step.subject} onChange={e => updateStep(idx, "subject", e.target.value)}
                          placeholder="Objet email" className="bg-transparent border-white/10 text-white text-xs" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Recurring config */}
            {autoForm.type === "recurring" && (
              <div className="space-y-2">
                <label className="text-[10px] text-slate-500 uppercase block">Frequence</label>
                <div className="flex gap-2">
                  {["weekly", "biweekly", "monthly"].map(f => (
                    <button key={f} onClick={() => setAutoForm(fm => ({ ...fm, recurring_schedule: { ...fm.recurring_schedule, frequency: f } }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium ${
                        autoForm.recurring_schedule.frequency === f ? "bg-[#3b82f6]/15 text-[#3b82f6] border border-[#3b82f6]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"
                      }`}>
                      {f === "weekly" ? "Hebdo" : f === "biweekly" ? "Bi-hebdo" : "Mensuel"}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <select value={autoForm.recurring_schedule.day}
                    onChange={e => setAutoForm(f => ({ ...f, recurring_schedule: { ...f.recurring_schedule, day: e.target.value } }))}
                    className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs">
                    {["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <Input type="time" value={autoForm.recurring_schedule.time}
                    onChange={e => setAutoForm(f => ({ ...f, recurring_schedule: { ...f.recurring_schedule, time: e.target.value } }))}
                    className="bg-[#0b0811] border-white/10 text-white text-xs w-28" />
                </div>
              </div>
            )}

            {/* Common: Subject + recipients */}
            {autoForm.type !== "sequence" && (
              <Input value={autoForm.subject} onChange={e => setAutoForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Objet de l'email" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            )}

            <select value={autoForm.recipients_type} onChange={e => setAutoForm(f => ({ ...f, recipients_type: e.target.value }))}
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs">
              <option value="all_clients">Tous les clients</option>
              <option value="tagged">Par tags</option>
              <option value="new_clients">Nouveaux clients (30 jours)</option>
              <option value="inactive">Clients inactifs (90+ jours)</option>
            </select>

            {/* A/B test option */}
            <div className="flex items-center justify-between p-2 bg-[#0b0811] border border-white/5 rounded-lg">
              <span className="text-xs text-slate-400 flex items-center gap-1"><Target className="w-3 h-3" /> A/B Test</span>
              <Toggle value={autoForm.ab_test?.enabled} onChange={v => setAutoForm(f => ({ ...f, ab_test: { ...f.ab_test, enabled: v } }))} testId="toggle-auto-ab" />
            </div>

            {autoForm.ab_test?.enabled && (
              <div className="space-y-2 p-2 bg-[#0b0811] border border-white/5 rounded-lg">
                <Input value={autoForm.ab_test?.variant_b_subject || ""} onChange={e => setAutoForm(f => ({ ...f, ab_test: { ...f.ab_test, variant_b_subject: e.target.value } }))}
                  placeholder="Objet variante B" className="bg-transparent border-white/10 text-white text-xs" />
              </div>
            )}

            {/* Enabled toggle */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-300">Activer immediatement</span>
              <Toggle value={autoForm.enabled} onChange={v => setAutoForm(f => ({ ...f, enabled: v }))} testId="toggle-auto-enabled" />
            </div>

            <Button onClick={saveAutomation} disabled={saving || !autoForm.name}
              className="w-full bg-[#3b82f6] text-white font-bold" data-testid="save-automation-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editAuto ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
